/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Components & Services
import { CustomerPaymentDetailsLedgerInfo } from "./info";
import { CustomerPaymentDetailsLedgerService } from "services/customer-payment-details-ledger";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

const initialValues = {
  unit: "",
  address: "",
  accountNumber: "",
  entityName: "",
  openingBalance: {
    debit: 0,
    credit: 0,
  },
  details: [
    {
      bookingDate: "",
      documentNumber: "",
      documentDate: "",
      description: "",
      correspondingAccount: "",
      discountPeriod: "",
      debitIncurred: 0,
      creditIncurred: 0,
      balanceDebit: 0,
      balanceCredit: 0,
    }
  ],
  totalIncurred: {
    debit: 0,
    credit: 0,
  },
  closingBalance: {
    debit: 0,
    credit: 0,
  },
  totalPages: "1",
  openingDate: "",
  reportDate: "",
  ledgerClerk: "",
  chiefAccountant: "",
  legalRepresentative: "",
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
};

function CustomerPaymentDetailsLedgerDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [formInitials, setFormInitials] = useState(initialValues);

  useEffect(() => {
    (async () => {
      try {
        if (id) {
          const response = await CustomerPaymentDetailsLedgerService.getById(id);
          const apiData = response.data || response;

          const formatDateVN = (iso) => {
            if (!iso) return "";
            if (typeof iso === "string" && iso.includes("/")) return iso;
            const d = new Date(iso);
            return isNaN(d) ? iso : `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
          };

          let [sDay, sMonth, sYear] = [initialValues.signingDay, initialValues.signingMonth, initialValues.signingYear];
          if (apiData.reportDate?.includes("/")) {
            const pts = apiData.reportDate.split("/");
            if (pts.length === 3) [sDay, sMonth, sYear] = pts;
          }

          setFormInitials({
            ...initialValues,
            unit: apiData.unit || "",
            address: apiData.address || "",
            accountNumber: apiData.accountNumber || "",
            entityName: apiData.entityName || "",
            openingBalance: apiData.openingBalance || { debit: 0, credit: 0 },
            details: (apiData.details || []).map(d => ({
              ...d,
              documentDate: formatDateVN(d.documentDate),
              debitIncurred: d.debitAmount || 0,
              creditIncurred: d.creditAmount || 0,
            })),
            totalIncurred: apiData.totalIncurred || { debit: 0, credit: 0 },
            closingBalance: apiData.closingBalance || { debit: 0, credit: 0 },
            totalPages: apiData.totalPages || "1",
            openingDate: formatDateVN(apiData.openingDate),
            reportDate: apiData.reportDate || "",
            signingDay: sDay,
            signingMonth: sMonth,
            signingYear: sYear,
            ledgerClerk: apiData.ledgerClerk || "",
            chiefAccountant: apiData.chiefAccountant || "",
            legalRepresentative: apiData.legalRepresentative || "",
          });
        } else {
          setFormInitials(initialValues);
        }
      } catch (e) {
        console.error("Load failed:", e);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const handleBack = () => navigation("/templates/customer-payment-details-ledger");

  const submitForm = async (fValues) => {
    try {
      const num = (v) => (v && v !== "" && !isNaN(v) ? Number(v) : 0);

      const payload = {
        unit: fValues.unit,
        address: fValues.address,
        accountNumber: fValues.accountNumber,
        entityName: fValues.entityName,
        openingBalance: {
          debit: num(fValues.openingBalance.debit),
          credit: num(fValues.openingBalance.credit),
        },
        details: (fValues.details || []).map(d => ({
          bookingDate: d.bookingDate,
          documentNumber: d.documentNumber,
          documentDate: d.documentDate,
          description: d.description,
          correspondingAccount: d.correspondingAccount,
          discountPeriod: d.discountPeriod,
          debitAmount: num(d.debitIncurred),
          creditAmount: num(d.creditIncurred),
          balanceDebit: num(d.balanceDebit),
          balanceCredit: num(d.balanceCredit),
        })),
        totalIncurred: {
          debit: num(fValues.totalIncurred.debit),
          credit: num(fValues.totalIncurred.credit),
        },
        closingBalance: {
          debit: num(fValues.closingBalance.debit),
          credit: num(fValues.closingBalance.credit),
        },
        totalPages: String(fValues.totalPages),
        openingDate: fValues.openingDate,
        reportDate: `${fValues.signingDay.padStart(2, "0")}/${fValues.signingMonth.padStart(2, "0")}/${fValues.signingYear}`,
        ledgerClerk: fValues.ledgerClerk,
        chiefAccountant: fValues.chiefAccountant,
        legalRepresentative: fValues.legalRepresentative,
      };

      if (!id) {
        await CustomerPaymentDetailsLedgerService.create(payload);
        toast.success("Tạo sổ thành công!");
      } else {
        await CustomerPaymentDetailsLedgerService.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
      navigation("/templates/customer-payment-details-ledger");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Lưu thất bại!");
    }
  };

  const handlePrint = (pValues) => {
    const html = getPrintTemplate(pValues);
    const win = window.open("", "_blank");
    win.document.open();
    win.document.write(`<html><head><title>Print S31-DN</title>
      <style>@media print{@page{size:A4 landscape;margin:1cm}}body{margin:0}</style>
      </head><body onload="setTimeout(()=>[window.print(),window.close()], 500)">${html}</body></html>`);
    win.document.close();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center"><Grid item xs={12}>
          <Formik initialValues={formInitials} enableReinitialize onSubmit={submitForm}>
            {({ values, isSubmitting, setFieldValue }) => (
              <Form autoComplete="off"><Card><SoftBox p={0}>
                <CustomerPaymentDetailsLedgerInfo formData={{ values, setFieldValue, containView }} />
                <SoftBox p={3} display="flex" justifyContent="space-between" mt={2}>
                  <SoftButton variant="gradient" color="light" onClick={handleBack}>Trở về</SoftButton>
                  {containView ? (
                    <SoftButton variant="gradient" color="success" onClick={() => handlePrint(values)}>In Sổ S31-DN</SoftButton>
                  ) : (
                    <SoftButton disabled={isSubmitting} type="submit" variant="gradient" color="dark">{id ? "Lưu" : "Tạo mới"}</SoftButton>
                  )}
                </SoftBox>
              </SoftBox></Card></Form>
            )}
          </Formik>
        </Grid></Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default CustomerPaymentDetailsLedgerDetail;
